import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { SUPABASE_URL } from "@/integrations/supabase/client";
import { apiFetch } from "@/lib/api-fetch";
import { formatApiError } from "@/lib/api-error";
import type { UserWithProfile } from "@/types/user-profile";

const MINIMUM_PASSWORD_LENGTH = 6;

interface ResetPasswordModalProps {
  user: UserWithProfile;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ResetPasswordModal({ user, open, onOpenChange }: ResetPasswordModalProps) {
  const { toast } = useToast();
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const userName = user.full_name || user.username || user.email || "este usuário";

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setPassword("");
      setConfirmation("");
    }
    onOpenChange(nextOpen);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (password.length < MINIMUM_PASSWORD_LENGTH) {
      toast({
        variant: "destructive",
        title: "Senha muito curta",
        description: `A senha deve ter pelo menos ${MINIMUM_PASSWORD_LENGTH} caracteres.`,
      });
      return;
    }
    if (password !== confirmation) {
      toast({
        variant: "destructive",
        title: "Senhas não coincidem",
        description: "A nova senha e a confirmação devem ser iguais.",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await apiFetch(`${SUPABASE_URL}admin/usuarios/${user.id}/senha`, {
        method: "PUT",
        body: JSON.stringify({ password }),
      });
      toast({
        title: "Senha redefinida",
        description: `Passe a nova senha para ${userName}.`,
      });
      handleOpenChange(false);
    } catch (error: unknown) {
      const { detail } = formatApiError(error);
      toast({
        variant: "destructive",
        title: "Não foi possível redefinir a senha",
        description: detail,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Redefinir senha</DialogTitle>
          <DialogDescription>
            Define uma nova senha para {userName}. A senha atual deixa de funcionar.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="reset-password">Nova senha</Label>
            <Input
              id="reset-password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              minLength={MINIMUM_PASSWORD_LENGTH}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="reset-password-confirmation">Confirmar nova senha</Label>
            <Input
              id="reset-password-confirmation"
              type="password"
              autoComplete="new-password"
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              minLength={MINIMUM_PASSWORD_LENGTH}
              required
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Salvando..." : "Redefinir senha"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
